/**
 * InventoryBar - Horizontal hotbar showing equipped items with add/remove/find operations.
 */

import { BaseUIComponent } from './BaseUIComponent.js';

import type { BaseUIComponentOptions, UIComponentMetadata } from './BaseUIComponent.js';
import type * as ENGINE from '../../index.js';

/**
 * Generic inventory item interface - can represent any type of item
 */
export interface InventoryItem {
  id: string;
  [key: string]: any; // Allow any additional properties
}

/**
 * Function to render an item in a slot
 */
export type ItemRenderer<T extends InventoryItem> = (item: T, slotElement: HTMLElement) => void;

/**
 * Function to clear a slot when empty
 */
export type SlotClearer = (slotElement: HTMLElement) => void;

export interface InventoryBarOptions<T extends InventoryItem = InventoryItem> extends BaseUIComponentOptions {
  /** Number of inventory slots */
  slotCount?: number;
  /** Size of each slot in pixels */
  slotSize?: number;
  /** Gap between slots in pixels */
  slotGap?: number;
  /** Custom item renderer function */
  itemRenderer?: ItemRenderer<T>;
  /** Custom slot clearer function */
  slotClearer?: SlotClearer;
  /** CSS class for filled slots */
  filledClass?: string;
  /** CSS class for empty slots */
  emptyClass?: string;
  /** CSS selector for item content within slot */
  itemContentSelector?: string;
}

export class InventoryBar<T extends InventoryItem = InventoryItem> extends BaseUIComponent<InventoryBarOptions<T>> {
  public static metadata: UIComponentMetadata = {
    displayName: 'Inventory Bar',
    category: 'inventory',
    summary: 'Horizontal hotbar showing equipped items with add / remove / find operations.',
    useCases: ['hotbar', 'inventory', 'quickslot', 'toolbar', 'equipment'],
    optionsType: 'InventoryBarOptions',
    assetPaths: {
      template: '@engine/assets/ui/templates/inventory-bar.html',
      styles: '@engine/assets/ui/styles/inventory-bar.css',
    },
  };

  private items: T[] = [];
  private slotElements: ENGINE.UIElement[] = [];

  protected override getAssetPaths() {
    return {
      templatePath: '@engine/assets/ui/templates/inventory-bar.html',
      stylesPath: '@engine/assets/ui/styles/inventory-bar.css'
    };
  }

  protected override getDefaultOptions(): Required<InventoryBarOptions<T>> {
    return {
      position: 'bottom-center',
      visible: true,
      customClasses: [],
      customStyles: {},
      slotCount: 5,
      slotSize: 56,
      slotGap: 8,
      itemRenderer: this.defaultItemRenderer.bind(this),
      slotClearer: this.defaultSlotClearer.bind(this),
      filledClass: 'filled',
      emptyClass: 'empty',
      itemContentSelector: '.item-content',
    };
  }

  protected override getInitialData() {
    const slots = Array.from({ length: this.options.slotCount }, (_, index) => ({
      index,
      isEmpty: true,
    }));

    const slotsHTML = slots.map(slot => `
      <div class="ui-inventory-bar-slot inventory-slot empty" data-slot="${slot.index}">
        <div class="item-content"></div>
        <div class="ui-inventory-bar-cooldown" data-cooldown-overlay></div>
        <span class="ui-inventory-bar-hotkey" data-hotkey>${slot.index + 1}</span>
      </div>
    `).join('');

    return {
      slots,
      slotSize: this.options.slotSize,
      slotGap: this.options.slotGap,
      slotsHTML,
    };
  }

  protected override cacheElements(): void {
    for (let i = 0; i < this.options.slotCount; i++) {
      const slotElement = this.layout?.querySelector(`.ui-inventory-bar-slot[data-slot="${i}"]`);
      if (slotElement) {
        this.elements.set(`slot-${i}`, this.createElementWrapper(slotElement));
        this.slotElements.push(this.createElementWrapper(slotElement));
      }
    }
  }

  /**
   * Default item renderer - displays item as text
   */
  private defaultItemRenderer(item: T, slotElement: HTMLElement): void {
    const contentElement = slotElement.querySelector(this.options.itemContentSelector) as HTMLElement;
    if (contentElement) {
      contentElement.textContent = item.id;
      contentElement.style.display = 'block';
      contentElement.title = `Item: ${item.id}`;
    }
  }

  /**
   * Default slot clearer - hides content
   */
  private defaultSlotClearer(slotElement: HTMLElement): void {
    const contentElement = slotElement.querySelector(this.options.itemContentSelector) as HTMLElement;
    if (contentElement) {
      contentElement.style.display = 'none';
      contentElement.textContent = '';
      contentElement.title = '';
    }
  }

  /**
   * Set the inventory items
   */
  public setItems(items: T[]): void {
    this.items = [...items];
    this.updateDisplay();
  }

  /**
   * Add an item to the inventory
   */
  public addItem(item: T): boolean {
    if (this.items.length >= this.options.slotCount) {
      return false; // Inventory full
    }

    this.items.push(item);
    this.updateDisplay();
    return true;
  }

  /**
   * Remove an item from the inventory by index
   */
  public removeItem(index: number): boolean {
    if (index < 0 || index >= this.items.length) {
      return false;
    }

    this.items.splice(index, 1);
    this.updateDisplay();
    return true;
  }

  /**
   * Remove an item from the inventory by item ID
   */
  public removeItemById(id: string): boolean {
    const index = this.items.findIndex(item => item.id === id);
    if (index >= 0) {
      return this.removeItem(index);
    }
    return false;
  }

  /**
   * Get the current inventory items
   */
  public getItems(): T[] {
    return [...this.items];
  }

  /**
   * Check if inventory is full
   */
  public isFull(): boolean {
    return this.items.length >= this.options.slotCount;
  }

  /**
   * Get the number of items in inventory
   */
  public getItemCount(): number {
    return this.items.length;
  }

  /**
   * Clear all items from inventory
   */
  public clearItems(): void {
    this.items = [];
    this.updateDisplay();
  }

  /**
   * Find an item by ID
   */
  public findItem(id: string): T | undefined {
    return this.items.find(item => item.id === id);
  }

  /**
   * Check if inventory contains an item with the given ID
   */
  public hasItem(id: string): boolean {
    return this.items.some(item => item.id === id);
  }

  private updateDisplay(): void {
    this.slotElements.forEach((slotElement, index) => {
      const item = this.items[index];
      const slot = slotElement.element;

      if (item) {
        slotElement.removeClass(this.options.emptyClass);
        slotElement.addClass(this.options.filledClass);
        this.options.itemRenderer(item, slot);
      } else {
        slotElement.removeClass(this.options.filledClass);
        slotElement.addClass(this.options.emptyClass);
        this.options.slotClearer(slot);
      }

      // Update hotkey chip from item.hotkey, falling back to slot index + 1.
      const hotkeyEl = slot.querySelector('[data-hotkey]') as HTMLElement | null;
      if (hotkeyEl) {
        const hotkey = item && typeof item.hotkey === 'string' ? item.hotkey : String(index + 1);
        hotkeyEl.textContent = hotkey;
      }

      // Update radial cooldown overlay from item.cooldownPercent (0-100).
      const cooldownPercent = item && typeof item.cooldownPercent === 'number'
        ? Math.max(0, Math.min(100, item.cooldownPercent))
        : 0;
      if (cooldownPercent > 0) {
        slot.dataset.cooldown = 'true';
        slot.style.setProperty('--ui-cooldown', String(cooldownPercent));
      } else {
        slot.dataset.cooldown = 'false';
        slot.style.removeProperty('--ui-cooldown');
      }
    });
  }

  /** Update the radial cooldown overlay for a single slot (0-100). */
  public setCooldown(slotIndex: number, percent: number): void {
    const slot = this.slotElements[slotIndex]?.element;
    if (!slot) return;
    const clamped = Math.max(0, Math.min(100, percent));
    if (clamped > 0) {
      slot.dataset.cooldown = 'true';
      slot.style.setProperty('--ui-cooldown', String(clamped));
    } else {
      slot.dataset.cooldown = 'false';
      slot.style.removeProperty('--ui-cooldown');
    }
  }
}